import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Course } from '@/lib/types';

interface CertificateRequirementsProps {
  course: Course;
  compact?: boolean;
  lessonProgress?: number;
}

export default function CertificateRequirements({
  course,
  compact = false,
  lessonProgress,
}: CertificateRequirementsProps) {
  const [open, setOpen] = useState(false);
  const passScore = Number(course.pass_score ?? 50);
  const meritScore = Number(course.merit_score ?? 80);
  const hasAssessments = (course.exams_count ?? 0) > 0 || (course.assignments_count ?? 0) > 0;

  const items = [
    'Complete every lesson',
    hasAssessments
      ? `Score at least ${passScore}% on each exam and graded assignment`
      : `Pass mark ${passScore}% if this course adds exams or assignments`,
    `Merit at ${meritScore}% overall`,
  ];

  if (compact) {
    return (
      <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-xs text-slate-600">
        <p className="font-semibold text-slate-800">Certificates</p>
        <ul className="mt-1.5 space-y-1 list-disc list-inside">
          {items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white text-sm text-slate-600 shadow-sm">
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left"
      >
        <span className="font-semibold text-slate-900">Certificate requirements</span>
        <span className="flex items-center gap-2">
          {lessonProgress != null && (
            <span className="text-xs text-slate-500">Lessons {Math.round(lessonProgress)}%</span>
          )}
          <svg
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true"
            className={`h-4 w-4 text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`}
          >
            <path
              fillRule="evenodd"
              d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.17l3.71-3.94a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      </button>
      {open && (
        <div className="px-4 pb-3">
          <ul className="space-y-1 list-disc list-inside">
            {items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
          <Link to="/certificates" className="mt-2 inline-block text-sm font-medium text-blue-700 hover:underline">
            View your certificates
          </Link>
        </div>
      )}
    </div>
  );
}
